import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Button } from "./ui/button";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Reusable "Send Invoice" confirmation dialog.
 *
 * Lets the user (a) verify or override the recipient email and (b) add
 * comma-separated CC recipients before actually firing the send.
 *
 * Props:
 *   open              - boolean
 *   onOpenChange      - (bool) => void
 *   invoiceId         - the invoice to send
 *   invoiceNumber     - optional display label (e.g. "INV-1023")
 *   defaultEmail      - pre-fills the "To" field (contact's saved email)
 *   onSent            - optional callback fired after a successful send
 */
export default function SendInvoiceDialog({
  open,
  onOpenChange,
  invoiceId,
  invoiceNumber = "",
  defaultEmail = "",
  onSent,
}) {
  const [recipient, setRecipient] = useState(defaultEmail || "");
  const [cc, setCc] = useState("");
  const [showCc, setShowCc] = useState(false);
  const [sending, setSending] = useState(false);

  // Re-sync when the dialog is re-opened for a different invoice
  useEffect(() => {
    if (open) {
      setRecipient(defaultEmail || "");
      setCc("");
      setShowCc(false);
    }
  }, [open, defaultEmail, invoiceId]);

  const handleSend = async () => {
    const to = (recipient || "").trim();
    if (!to) {
      toast.error("Recipient email is required");
      return;
    }
    setSending(true);
    try {
      const { data } = await axios.post(`${API_URL}/invoices/${invoiceId}/send`, {
        recipient_email: to,
        cc_emails: cc,
      });
      const ccPreview = (cc || "").trim();
      toast.success(
        data?.message ||
          (ccPreview ? `Invoice sent to ${to} (cc: ${ccPreview})` : `Invoice sent to ${to}`)
      );
      onOpenChange(false);
      if (onSent) onSent();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send invoice");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md w-[95vw] p-4 sm:p-6" data-testid="send-invoice-dialog">
        <DialogHeader>
          <DialogTitle>Send Invoice{invoiceNumber ? ` ${invoiceNumber}` : ""}</DialogTitle>
          <DialogDescription>Confirm the recipient — add anyone else you want on the email.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-3">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>To *</Label>
              {!showCc && (
                <button
                  type="button"
                  onClick={() => setShowCc(true)}
                  className="text-xs font-medium text-[#0F4C81] hover:text-[#0D4270]"
                  data-testid="invoice-send-add-cc-btn"
                >
                  + Add CC
                </button>
              )}
            </div>
            <Input
              type="email"
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              placeholder="customer@email.com"
              className="h-11"
              data-testid="invoice-send-recipient-input"
            />
          </div>
          {showCc && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>CC (comma-separated)</Label>
                <button
                  type="button"
                  onClick={() => { setCc(""); setShowCc(false); }}
                  className="text-xs text-slate-400 hover:text-slate-600"
                  data-testid="invoice-send-remove-cc-btn"
                >
                  remove
                </button>
              </div>
              <Input
                type="text"
                value={cc}
                onChange={(e) => setCc(e.target.value)}
                placeholder="spouse@email.com, partner@email.com"
                className="h-11"
                data-testid="invoice-send-cc-input"
              />
              <p className="text-xs text-slate-400">Everyone gets the same email with the same pay link.</p>
            </div>
          )}
        </div>
        <div className="mt-5 flex gap-2">
          <Button
            className="flex-1 h-11 bg-[#0F4C81] hover:bg-[#0D4270]"
            onClick={handleSend}
            disabled={sending || !recipient}
            data-testid="invoice-send-confirm-btn"
          >
            {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            Send
          </Button>
          <Button
            variant="ghost"
            className="h-11"
            onClick={() => onOpenChange(false)}
            disabled={sending}
            data-testid="invoice-send-cancel-btn"
          >
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
